import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { forwardRef, useEffect, useState, } from "react";
import { FilterLines } from "@hl/base-components/lib/assets/icons.generated";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { OUTLINE_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Button, createStyles, TextInput, Text, Flex, useMantineTheme, } from "@mantine/core";
import { IconSearch } from "@tabler/icons";
import useDetectSticky from "../../../hooks/useDetectSticky";
const useStickyStyles = createStyles((theme) => ({
    stickyContainer: {
        position: "sticky",
        background: theme.colors.baseBackground[0],
        transition: "top 250ms ease 0s",
    },
}));
export const StickyContainer = forwardRef(({ children, containerConfig = {
    top: "calc(var(--mantine-header-height, 0px) + 1px)",
    zIndex: 99,
}, onStickyChange, sx, ...props }, ref) => {
    var _a, _b;
    const { isLarge } = useMantineMediaQueries({ withInitialValues: true });
    const [isStickyScroll, setIsStickyScroll] = useState(false);
    const [headerHeight, setHeaderHeight] = useState("0px");
    const { isSticky, ref: stickyRef } = useDetectSticky(undefined, {
        threshold: [1],
        rootMargin: `-${headerHeight} 0px 0px 0px`, // Re-added from original
    });
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const theme = useMantineTheme();
    // Re-added header height calculation from original
    useEffect(() => {
        const height = getComputedStyle(document.documentElement).getPropertyValue("--mantine-header-height");
        const adjustedHeight = parseInt(height.split("px")[0]) + 2;
        if (height) {
            setHeaderHeight(String(adjustedHeight) + "px");
        }
    }, []);
    const combinedRef = (instance) => {
        if (typeof ref === "function")
            ref(instance);
        else if (ref)
            ref.current = instance;
        stickyRef.current = instance;
    };
    useEffect(() => {
        onStickyChange === null || onStickyChange === void 0 ? void 0 : onStickyChange(isSticky);
    }, [isSticky, onStickyChange]);
    useEffect(() => {
        const handleScroll = () => {
            const currentScroll = window.pageYOffset;
            setIsStickyScroll(currentScroll > lastScrollTop);
            setLastScrollTop(currentScroll <= 0 ? 0 : currentScroll);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [lastScrollTop]);
    const dynamicTop = isSticky
        ? isStickyScroll && !isLarge
            ? `calc(var(--mantine-header-height, 0px) - ${(_b = (_a = stickyRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) !== null && _b !== void 0 ? _b : 120}px)`
            : `calc(var(--mantine-header-height, 0px))`
        : undefined;
    return (_jsx(Flex, { ref: combinedRef, sx: {
            position: "sticky",
            background: theme.colors.baseBackground[0],
            transition: "top 250ms ease 0s",
            borderBottom: `1px solid ${isSticky ? theme.colors.divider[0] : "transparent"}`,
            ...containerConfig,
            ...(typeof sx === "function" ? sx(theme) : sx),
            top: dynamicTop,
        }, ...props, children: children }));
});
export const FilterButton = ({ numberOfTraitsSelected, onClick, icon: Icon, }) => {
    const { theme } = useStickyStyles();
    return (_jsx(Button, { size: "lg", color: OUTLINE_COLOR, onClick: onClick, rightIcon: numberOfTraitsSelected ? (_jsx(Text, { px: 6, fw: WEIGHT_BOLD, bg: theme.colors.secondaryBackground[0], sx: { borderRadius: 999, display: "flex", alignItems: "center" }, h: 20, color: TEXT_COLOR.PLACEHOLDER, size: 12, children: numberOfTraitsSelected })) : undefined, children: Icon ? (_jsx(Icon, { width: 16, height: 16 })) : (_jsx(FilterLines, { width: 16, height: 16 })) }));
};
const useStyles = createStyles({
    input: { height: "40px", marginTop: 0, fontWeight: WEIGHT_BOLD },
    root: { flexGrow: 1, width: "100%" },
});
export const SearchInput = ({ value, onChange, placeholder, classNames, ...props }) => {
    const { classes, theme } = useStyles();
    return (_jsx(TextInput, { placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : "Filter tokens by name or ID", value: value, onChange: (e) => onChange(e.currentTarget.value), icon: _jsx(IconSearch, { width: 16, height: 16, color: theme.colors.primaryText[0] }), classNames: {
            input: classes.input,
            root: classes.root,
            ...classNames,
        }, ...props }));
};
export const MarketplaceStickyHeader = forwardRef(({ children, sx, ...containerProps }, ref) => {
    const sxStyles = sx;
    return (_jsx(StickyContainer, { ref: ref, gap: "sm", wrap: "wrap", justify: "space-between", align: "center", sx: [
            (theme) => ({
                padding: 16,
                [`@media (max-width: ${theme.breakpoints.md}px)`]: {
                    paddingLeft: containerProps.isCollectorsChoice ? 40 : 20,
                    paddingRight: containerProps.isCollectorsChoice ? 40 : 20,
                    marginLeft: containerProps.isCollectorsChoice ? -40 : -20,
                    marginRight: containerProps.isCollectorsChoice ? -40 : -20,
                },
                marginLeft: -40,
                marginRight: -40,
                paddingLeft: 40,
                paddingRight: 40,
            }),
            sxStyles,
        ], ...containerProps, children: children }));
});
